import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Star, Award, TrendingUp, Shield, Users, Clock, Smartphone, Globe, Database, Code, ExternalLink, Github, FileText, Eye, Calendar } from 'lucide-react';
import Layout from '../layouts/Layout';
import { Link } from 'react-router-dom';
import CTA from '../components/CTA';
import apiClient from '../helpers/apiClient';

const HomePage = () => {
	const [activeTestimonial, setActiveTestimonial] = useState(0);
	const [portfolioItems, setPortfolioItems] = useState([]);
	const [portfolioLoading, setPortfolioLoading] = useState(true);
	const [newsItems, setNewsItems] = useState([]);
	const [newsLoading, setNewsLoading] = useState(true);

	useEffect(() => {
		getPortfolio();
		getNews();
	}, []);

	const getPortfolio = async () => {
		try {
			setPortfolioLoading(true);

			// Fetch featured portfolios with limit
			const response = await apiClient.getPortfolio(true, 3);

			if (response.data) {
				setPortfolioItems(response.data.sort((a, b) => b.id - a.id) || []);
			} else {
				console.error('Error fetching portfolio:', response.error);
			}
		} catch (e) {
			console.error('Error getting portfolio items: ', e);
		} finally {
			setPortfolioLoading(false);
		}
	};

	const getNews = async () => {
		try {
			setNewsLoading(true);

			// Fetch featured news with limit
			const response = await apiClient.getFeaturedNews(3);

			if (response.success) {
				setNewsItems(response.data || []);
			} else {
				console.error('Error fetching news:', response.error);
			}
		} catch (e) {
			console.error('Error getting news items: ', e);
		} finally {
			setNewsLoading(false);
		}
	};

	const formatCategory = (category) => {
		if (!category) return 'Project';
		return category.split('-').map(word =>
			word.charAt(0).toUpperCase() + word.slice(1)
		).join(' ');
	};

	const formatDate = (dateString) => {
		if (!dateString) return '';
		try {
			return new Date(dateString).toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'long',
				year: 'numeric'
			});
		} catch {
			return '';
		}
	};

	const getProjectIcon = (category) => {
		switch (category) {
			case 'web-development':
				return <Globe className="w-8 h-8 text-blue-600" />;
			case 'mobile-app':
				return <Smartphone className="w-8 h-8 text-green-600" />;
			case 'e-commerce':
				return <TrendingUp className="w-8 h-8 text-purple-600" />;
			case 'ui-ux':
				return <Award className="w-8 h-8 text-orange-600" />;
			default:
				return <Code className="w-8 h-8 text-slate-600" />;
		}
	};

	const getProjectGradient = (category) => {
		switch (category) {
			case 'web-development':
				return 'from-blue-50 to-blue-100';
			case 'mobile-app':
				return 'from-green-50 to-green-100';
			case 'e-commerce':
				return 'from-purple-50 to-purple-100';
			case 'ui-ux':
				return 'from-orange-50 to-orange-100';
			default:
				return 'from-slate-50 to-slate-100';
		}
	};

	const services = [
		{
			icon: <Globe className="w-6 h-6" />,
			title: "Enterprise Web Development",
			description: "Sophisticated web applications architected for scale, security, and performance at the enterprise level.",
			technologies: ["React", "Node.js", "Enterprise APIs", "Cloud Infrastructure"],
			metric: "99.9% Uptime",
			deliverable: "Scalable platforms that grow with your business",
			url: "/services/web-development"
		},
		{
			icon: <Smartphone className="w-6 h-6" />,
			title: "Mobile Solutions",
			description: "Cross-platform mobile applications that deliver exceptional user experiences and drive engagement.",
			technologies: ["React Native", "iOS", "Android", "Performance Optimisation"],
			metric: "95% User Retention",
			deliverable: "Native-quality apps across all platforms",
			url: "/services/mobile-development"
		},
		{
			icon: <Database className="w-6 h-6" />,
			title: "System Architecture",
			description: "Robust backend infrastructure and database solutions designed for enterprise-grade reliability.",
			technologies: ["MySQL", "PostgreSQL", "Microservices", "API Design"],
			metric: "40% Cost Reduction",
			deliverable: "Future-proof systems that scale effortlessly",
			url: "/services/system-architecture"
		},
		{
			icon: <Shield className="w-6 h-6" />,
			title: "Digital Transformation",
			description: "Complete modernisation of legacy systems with cutting-edge technology and best practices.",
			technologies: ["Full-Stack", "Cloud Migration", "Integration", "Security"],
			metric: "60% Efficiency Gain",
			deliverable: "Seamless transition to modern infrastructure",
			url: "/services/digital-transformation"
		}
	];

	const testimonials = [
		{
			name: "James Morrison",
			company: "Apex Financial Group",
			role: "Chief Technology Officer",
			text: "KGC Innovations transformed our entire digital infrastructure. Their technical expertise and strategic approach delivered exceptional results that exceeded all our performance targets.",
			rating: 5,
			industry: "Financial Services"
		},
		{
			name: "Sarah Chen",
			company: "MedTech Solutions Ltd",
			role: "Head of Digital Innovation",
			text: "Working with KGC was transformational. They understood our complex requirements and delivered a solution that revolutionised our patient management system.",
			rating: 5,
			industry: "Healthcare Technology"
		},
		{
			name: "David Thompson",
			company: "Sterling Enterprises",
			role: "Managing Director",
			text: "Professional, reliable, and innovative. KGC Innovations consistently delivers enterprise-grade solutions that drive real business value.",
			rating: 5,
			industry: "Enterprise Software"
		}
	];

	useEffect(() => {
		const interval = setInterval(() => {
			setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
		}, 8000);
		return () => clearInterval(interval);
	}, [testimonials.length]);

	return (
		<Layout
			title="Enterprise Web & Mobile Development"
			description="Expert web & mobile app development services in the UK. 10+ years experience in React, Node.js, PHP, React Native. Custom solutions for businesses."
			keywords="web development UK, mobile app development, React development, Node.js developer, PHP development"
			path="/"
		>
			{/* Hero Section */}
			<section id="home" className="relative pt-32 pb-20 px-6">
				<div className="max-w-6xl mx-auto">
					<div className="grid lg:grid-cols-12 gap-12 items-center">
						<div className="lg:col-span-7">
							<div className="inline-flex items-center px-4 py-2 bg-slate-50 border border-slate-200 rounded-full text-sm text-slate-700 mb-8">
								<Award className="w-4 h-4 mr-2 text-slate-500" />
								Trusted by Enterprise Leaders
							</div>

							<h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
								Enterprise Technology
								<span className="block text-slate-700 mt-2">That Delivers Results</span>
							</h1>

							<p className="text-xl text-slate-600 mb-10 leading-relaxed">
								We architect and develop sophisticated technology solutions that drive measurable business growth for forward-thinking organisations across the UK.
							</p>

							<div className="flex flex-col sm:flex-row gap-4 mb-12">
								<Link
									to={'/schedule-consultation'}
									className="bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg flex items-center justify-center"
								>
									Schedule Consultation <ArrowRight className="ml-2 w-5 h-5" />
								</Link>
							</div>

							<div className="grid grid-cols-3 gap-8 pt-8 border-t border-slate-200">
								<div>
									<div className="text-3xl font-bold text-slate-900">10+</div>
									<div className="text-slate-600">Years Experience</div>
								</div>
								<div>
									<div className="text-3xl font-bold text-slate-900">200+</div>
									<div className="text-slate-600">Projects Delivered</div>
								</div>
								<div>
									<div className="text-3xl font-bold text-slate-900">£50M+</div>
									<div className="text-slate-600">Value Generated</div>
								</div>
							</div>
						</div>

						<div className="lg:col-span-5">
							<div className="relative">
								<div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl transform rotate-3"></div>
								<div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-slate-200">
									<div className="flex items-center justify-between mb-6">
										<div className="flex space-x-2">
											<div className="w-3 h-3 bg-red-400 rounded-full"></div>
											<div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
											<div className="w-3 h-3 bg-green-400 rounded-full"></div>
										</div>
										<span className="text-xs text-slate-500 font-mono">enterprise-solution.ts</span>
									</div>

									<div className="space-y-4 font-mono text-sm">
										<div className="text-slate-600">
											<span className="text-purple-600">interface</span> <span className="text-blue-600">EnterpriseSolution</span>
										</div>
										<div className="text-slate-600 pl-4">
											performance: <span className="text-green-600">'optimised'</span>;
										</div>
										<div className="text-slate-600 pl-4">
											security: <span className="text-green-600">'enterprise-grade'</span>;
										</div>
										<div className="text-slate-600 pl-4">
											scalability: <span className="text-green-600">'unlimited'</span>;
										</div>
										<div className="text-slate-600 pl-4">
											support: <span className="text-green-600">'24/7'</span>;
										</div>
										<div className="text-slate-600"></div>

										<div className="pt-4 text-slate-600">
											<span className="text-purple-600">export</span> <span className="text-purple-600">const</span> solution =
										</div>
										<div className="text-slate-600 pl-4">
											<span className="text-blue-600">KGCInnovations</span>.<span className="text-yellow-600">create</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Services Section */}
			<section id="services" className="relative py-24 bg-slate-50">
				<div className="max-w-6xl mx-auto px-6">
					<div className="text-center mb-16">
						<h2 className="text-4xl font-bold text-slate-900 mb-6">Our Core Expertise</h2>
						<p className="text-xl text-slate-600 max-w-3xl mx-auto">
							Comprehensive technology solutions engineered for enterprise success
						</p>
					</div>

					<div className="grid lg:grid-cols-2 gap-8">
						{services.map((service, index) => (
							<div
								key={index}
								className="group bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
							>
								<div className="flex items-start justify-between mb-6">
									<div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center text-white">
										{service.icon}
									</div>
									<div className="text-right">
										<div className="text-sm font-bold text-slate-900">{service.metric}</div>
										<div className="text-xs text-slate-500">Average Result</div>
									</div>
								</div>

								<h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
								<p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>

								<div className="mb-6">
									<div className="text-sm font-medium text-slate-900 mb-2">Key Deliverable</div>
									<div className="text-sm text-slate-600">{service.deliverable}</div>
								</div>

								<div className="flex flex-wrap gap-2 mb-6">
									{service.technologies.map((tech, techIndex) => (
										<span
											key={techIndex}
											className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm"
										>
											{tech}
										</span>
									))}
								</div>

								<Link to={service.url} className="flex items-center text-slate-600 group-hover:text-slate-900 transition-colors">
									<span className="text-sm font-medium">Learn more</span>
									<ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
								</Link>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* About Section */}
			<section id="about" className="relative py-24 bg-white">
				<div className="max-w-6xl mx-auto px-6">
					<div className="grid lg:grid-cols-2 gap-16 items-center">
						<div>
							<h2 className="text-4xl font-bold text-slate-900 mb-8">
								A Decade of Technical Excellence
							</h2>
							<p className="text-lg text-slate-600 mb-8 leading-relaxed">
								Founded in 2014, KGC Innovations Ltd has established itself as a trusted technology partner for enterprises across the UK. We combine deep technical expertise with strategic business insight to deliver solutions that drive real organisational transformation.
							</p>

							<div className="space-y-6 mb-8">
								{[
									"Enterprise-grade security and compliance standards",
									"Scalable architecture designed for future growth",
									"Agile methodology with transparent project delivery",
									"Comprehensive support and maintenance programs"
								].map((point, index) => (
									<div key={index} className="flex items-center">
										<CheckCircle className="w-5 h-5 text-green-600 mr-4 flex-shrink-0" />
										<span className="text-slate-700">{point}</span>
									</div>
								))}
							</div>

							<div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
								<div className="flex items-center mb-4">
									<TrendingUp className="w-5 h-5 text-slate-700 mr-3" />
									<span className="font-semibold text-slate-900">Client Success Metrics</span>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<div className="text-2xl font-bold text-slate-900">99.9%</div>
										<div className="text-sm text-slate-600">System Uptime</div>
									</div>
									<div>
										<div className="text-2xl font-bold text-slate-900">98%</div>
										<div className="text-sm text-slate-600">Client Retention</div>
									</div>
								</div>
							</div>
						</div>

						<div className="space-y-8">
							<div className="bg-slate-50 rounded-xl p-8 border border-slate-200">
								<h3 className="text-xl font-bold text-slate-900 mb-6">Technology Stack</h3>
								<div className="grid grid-cols-3 gap-3">
									{[
										'React', 'Node.js', 'PHP', 'MySQL', 'JavaScript', 'React Native',
										'TypeScript', 'WordPress', 'AWS', 'Docker', 'API Design', 'Security'
									].map((tech, index) => (
										<div key={index} className="bg-white rounded-lg p-3 text-center border border-slate-200">
											<span className="text-slate-700 text-sm font-medium">{tech}</span>
										</div>
									))}
								</div>
							</div>

							<div className="bg-slate-900 text-white rounded-xl p-8">
								<h3 className="text-xl font-bold mb-6">Our Commitment</h3>
								<div className="space-y-4">
									<div className="flex items-center">
										<Shield className="w-5 h-5 mr-3" />
										<span>Enterprise-grade security in every solution</span>
									</div>
									<div className="flex items-center">
										<Clock className="w-5 h-5 mr-3" />
										<span>On-time delivery with transparent reporting</span>
									</div>
									<div className="flex items-center">
										<Users className="w-5 h-5 mr-3" />
										<span>Dedicated support throughout the project lifecycle</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Portfolio Preview */}
			<section id="portfolio" className="relative py-24 bg-slate-50">
				<div className="max-w-6xl mx-auto px-6">
					<div className="text-center mb-16">
						<h2 className="text-4xl font-bold text-slate-900 mb-6">Featured Projects</h2>
						<p className="text-xl text-slate-600">Real results for real businesses</p>
					</div>

					{portfolioLoading ? (
						<div className="grid md:grid-cols-3 gap-8">
							{[...Array(3)].map((_, index) => (
								<div key={index} className="bg-white rounded-2xl border border-slate-200 overflow-hidden animate-pulse">
									<div className="h-48 bg-slate-200"></div>
									<div className="p-6 space-y-4">
										<div className="h-4 bg-slate-200 rounded w-1/3"></div>
										<div className="h-6 bg-slate-200 rounded w-3/4"></div>
										<div className="h-4 bg-slate-200 rounded w-1/2"></div>
										<div className="h-4 bg-slate-200 rounded w-2/3"></div>
										<div className="flex gap-2">
											<div className="h-6 bg-slate-200 rounded w-16"></div>
											<div className="h-6 bg-slate-200 rounded w-20"></div>
										</div>
									</div>
								</div>
							))}
						</div>
					) : portfolioItems.length > 0 ? (
						<div className="grid md:grid-cols-3 gap-8 mb-12">
							{portfolioItems.map((project, index) => (
								<Link
									key={project.id}
									to={`/portfolio/${project.slug}`}
									className="group cursor-pointer"
								>
									<div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
										<div className={`h-48 bg-gradient-to-br ${getProjectGradient(project.category)} flex items-center justify-center relative overflow-hidden`}>
											{project.featured_image ? (
												<img
													src={project.featured_image}
													alt={project.title}
													className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
													onError={(e) => {
														e.target.style.display = 'none';
														e.target.nextSibling.style.display = 'flex';
													}}
												/>
											) : null}
											<div className={`w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center ${project.featured_image ? 'absolute' : ''}`}>
												{getProjectIcon(project.category)}
											</div>
											{project.featured && (
												<div className="absolute top-4 left-4">
													<span className="px-2 py-1 bg-emerald-500 text-white text-xs rounded-full font-medium">
														Featured
													</span>
												</div>
											)}
										</div>

										<div className="p-6">
											<div className="flex items-center gap-2 mb-3">
												{project.category && (
													<span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">
														{formatCategory(project.category)}
													</span>
												)}
												{project.client_name && (
													<span className="text-slate-500 text-xs">
														• {project.client_name}
													</span>
												)}
											</div>

											<h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-slate-700 transition-colors">
												{project.title}
											</h3>

											{project.short_description && (
												<p className="text-slate-600 text-sm mb-4 line-clamp-2">
													{project.short_description}
												</p>
											)}

											<div className="flex flex-wrap gap-2 mb-4">
												{Array.isArray(project.technologies) && project.technologies.slice(0, 3).map((tech, techIndex) => (
													<span key={techIndex} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
														{tech}
													</span>
												))}
												{Array.isArray(project.technologies) && project.technologies.length > 3 && (
													<span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
														+{project.technologies.length - 3}
													</span>
												)}
											</div>

											<div className="flex items-center justify-between">
												<div className="flex gap-2">
													{project.project_url && (
														<ExternalLink className="w-4 h-4 text-slate-400" />
													)}
													{project.github_url && (
														<Github className="w-4 h-4 text-slate-400" />
													)}
												</div>
												<div className="flex items-center text-slate-600 group-hover:text-slate-900 transition-colors">
													<span className="text-sm font-medium">View Project</span>
													<ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
												</div>
											</div>
										</div>
									</div>
								</Link>
							))}
						</div>
					) : (
						<div className="text-center py-12">
							<div className="text-slate-400 text-4xl mb-4">📁</div>
							<p className="text-slate-600">No featured projects available at the moment.</p>
						</div>
					)}

					<div className="text-center">
						<Link
							to="/portfolio"
							className="inline-flex items-center bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg"
						>
							View All Projects <ArrowRight className="ml-2 w-5 h-5" />
						</Link>
					</div>
				</div>
			</section>

			{/* News & Insights Preview */}
			<section id="news" className="relative py-24 bg-white">
				<div className="max-w-6xl mx-auto px-6">
					<div className="text-center mb-16">
						<h2 className="text-4xl font-bold text-slate-900 mb-6">Latest Insights</h2>
						<p className="text-xl text-slate-600">Stay updated with industry trends and expert knowledge</p>
					</div>

					{newsLoading ? (
						<div className="grid md:grid-cols-3 gap-8">
							{[...Array(3)].map((_, index) => (
								<div key={index} className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden animate-pulse">
									<div className="h-48 bg-slate-200"></div>
									<div className="p-6 space-y-4">
										<div className="h-4 bg-slate-200 rounded w-1/3"></div>
										<div className="h-6 bg-slate-200 rounded w-3/4"></div>
										<div className="h-4 bg-slate-200 rounded w-full"></div>
										<div className="h-4 bg-slate-200 rounded w-2/3"></div>
									</div>
								</div>
							))}
						</div>
					) : newsItems.length > 0 ? (
						<div className="grid md:grid-cols-3 gap-8 mb-12">
							{newsItems.map((article, index) => (
								<Link
									key={article.id}
									to={`/news/${article.slug}`}
									className="group cursor-pointer"
								>
									<div className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
										<div className="h-48 bg-slate-100 overflow-hidden">
											{article.featured_image ? (
												<img
													src={article.featured_image}
													alt={article.title}
													className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
													onError={(e) => {
														e.target.style.display = 'none';
														e.target.nextSibling.style.display = 'flex';
													}}
												/>
											) : null}
											<div className={`w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center ${article.featured_image ? 'hidden' : ''}`}>
												<FileText className="w-12 h-12 text-slate-400" />
											</div>
										</div>

										<div className="p-6">
											<div className="flex items-center gap-3 mb-4">
												{article.category && (
													<span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
														{article.category}
													</span>
												)}
												<div className="flex items-center text-slate-500 text-sm">
													<Calendar className="w-4 h-4 mr-1" />
													{formatDate(article.published_at || article.created_at)}
												</div>
											</div>

											<h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-slate-700 transition-colors line-clamp-2">
												{article.title}
											</h3>

											{article.excerpt && (
												<p className="text-slate-600 text-sm mb-4 line-clamp-2">
													{article.excerpt}
												</p>
											)}

											<div className="flex items-center justify-between">
												<div className="flex items-center gap-3 text-slate-500 text-sm">
													{article.reading_time && (
														<div className="flex items-center">
															<Clock className="w-4 h-4 mr-1" />
															{article.reading_time} min
														</div>
													)}
													{article.views !== undefined && (
														<div className="flex items-center">
															<Eye className="w-4 h-4 mr-1" />
															{article.views}
														</div>
													)}
												</div>
												<div className="flex items-center text-slate-600 group-hover:text-slate-900 transition-colors">
													<span className="text-sm font-medium">Read Article</span>
													<ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
												</div>
											</div>
										</div>
									</div>
								</Link>
							))}
						</div>
					) : (
						<div className="text-center py-12">
							<div className="text-slate-400 text-4xl mb-4">📰</div>
							<p className="text-slate-600">No articles available at the moment.</p>
						</div>
					)}

					<div className="text-center">
						<Link
							to="/news"
							className="inline-flex items-center bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg"
						>
							View All Articles <ArrowRight className="ml-2 w-5 h-5" />
						</Link>
					</div>
				</div>
			</section>

			{/* Testimonials */}
			<section className="relative py-24 bg-white">
				<div className="max-w-4xl mx-auto px-6">
					<div className="text-center mb-16">
						<h2 className="text-4xl font-bold text-slate-900 mb-6">Client Testimonials</h2>
						<p className="text-xl text-slate-600">Trusted by industry leaders across sectors</p>
					</div>

					<div className="relative">
						<div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
							<div className="flex justify-center mb-6">
								{[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
									<Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
								))}
							</div>

							<blockquote className="text-lg text-slate-700 mb-8 italic leading-relaxed text-center">
								"{testimonials[activeTestimonial].text}"
							</blockquote>

							<div className="text-center">
								<div className="font-bold text-slate-900 text-lg mb-1">
									{testimonials[activeTestimonial].name}
								</div>
								<div className="text-slate-600 mb-1">
									{testimonials[activeTestimonial].role}
								</div>
								<div className="font-medium text-slate-900">
									{testimonials[activeTestimonial].company}
								</div>
								<div className="text-sm text-slate-500 mt-1">
									{testimonials[activeTestimonial].industry}
								</div>
							</div>
						</div>

						<div className="flex justify-center mt-8 space-x-2">
							{testimonials.map((_, index) => (
								<button
									key={index}
									onClick={() => setActiveTestimonial(index)}
									className={`w-3 h-3 rounded-full transition-all ${activeTestimonial === index ? 'bg-slate-900' : 'bg-slate-300'
										}`}
									aria-label={`View testimonial ${index + 1}`}
								/>
							))}
						</div>
					</div>
				</div>
			</section>

			<CTA />
		</Layout>
	);
};

export default HomePage;